/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './creative-attach-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {bulkApiErrorsFromResponse} from '../common/controller/bulk-action-errors';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-attach-modal',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreativeAttachModalComponent implements OnInit {
  list: any;
  Creative: any;
  onComplete: (updatedCreatives?: any, bulkSelection?: any) => void;

  attach = 'LINE_ITEM';
  hasCreativeSet = false;
  attachSuffix = 'Line Item';
  noGoodMessage: string;
  warningMessage: string;
  adv: any;
  private readonly formState = signal<any>({}, {equal: () => false});
  get form(): any { return this.formState(); }
  set form(value: any) { this.formState.set(value); }
  lineItem: any;
  creativeSet: any;

  private workingSelections: any[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.hasCreativeSet = this.localNetworkInfo.getInfo('creativeSetCount') > 0;
    this.attachSuffix = 'Line Item' + (this.hasCreativeSet ? ' or Creative Set' : '');

    this.workingSelections = _.filter(this.list.bulkSelection, (val: any) => {
      return !_.get(val, ['creativeSet', 'id']) && !_.get(val, ['lineItem', 'id']);
    });

    const advs = _.uniqBy(_.filter(_.map(this.workingSelections, (val: any) => {
      return _.get(val, ['advertiser']);
    }), (f: any) => {
      return _.get(f, ['id']);
    }), 'id');

    if (advs.length > 1) {
      this.noGoodMessage = 'The creatives you selected belong to more than one advertiser. You cannot attach all these creatives to the same line item.';
    } else if (advs.length === 1) {
      this.adv = advs[0];
    }

    const bulkSelectionLength = Object.keys(this.list.bulkSelection).length;
    if (this.workingSelections.length === 0) {
      this.noGoodMessage = 'All the selected creatives already have a parent attached. These creatives cannot be attached to another parent.';
    } else if (this.workingSelections.length !== bulkSelectionLength) {
      const dropped = bulkSelectionLength - this.workingSelections.length;
      this.warningMessage = dropped + ' of your ' + bulkSelectionLength + ' selected creatives are already attached to a parent and won\'t be include in the bulk update.';
    }
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  attachParent(): void {
    this.form.apiErrors = [];
    this.form.bulkError = false;
    const changedObjects = _.map(this.workingSelections, (c: any) => {
      const param = this.attach === 'LINE_ITEM' ? 'lineItem' : 'creativeSet';
      _.set(c, [param, 'id'], _.get(this, [param, 'id']));
      return _.pick(c, ['id', param]);
    });
    firstValueFrom(this.Creative.saveMany(changedObjects)).then(() => {
      this.list.disabled = false;
      this.adnModalLoading.removeModalLoading();
      this.onComplete?.(undefined, this.list.bulkSelection);
      this.bsModalRef.hide();
    }, (response: any) => {
      this.adnModalLoading.removeModalLoading();
      this.list.disabled = false;
      this.form.apiErrors = bulkApiErrorsFromResponse(response);
      this.form.bulkError = true;
      this.form = this.form;
    });
  }
}
